import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BrandLogo } from "@/components/shared/BrandLogo";
import {
  Sun, Coffee, Utensils, Sunset, Moon, GripVertical,
  Plus, X, ChevronLeft, ChevronRight, Clock, DollarSign,
} from "lucide-react";
import SchedulerHelpSheet from "./SchedulerHelpSheet";

const SLOTS = [
  { id: "morning", label: "Morning", time: "8am–11am", icon: Coffee, color: "bg-amber-50 border-amber-200 text-amber-700" },
  { id: "lunch", label: "Lunch", time: "11am–2pm", icon: Utensils, color: "bg-orange-50 border-orange-200 text-orange-700" },
  { id: "afternoon", label: "Afternoon", time: "2pm–5pm", icon: Sun, color: "bg-sky-50 border-sky-200 text-sky-700" },
  { id: "evening", label: "Evening", time: "5pm–9pm", icon: Sunset, color: "bg-indigo-50 border-indigo-200 text-indigo-700" },
];

const CAT_SLOT = {
  "Coffee": "morning", "Coffee/Cafe": "morning", "Boba/Tea": "afternoon",
  "Bakery": "morning", "Bakery/Cafe": "morning",
  "Fast Food": "lunch", "Fast Casual": "lunch",
  "Restaurant": "evening", "Restaurant/Buffet": "evening",
  "Dessert": "afternoon", "Dessert/Ice Cream": "afternoon", "Ice Cream / Dessert": "afternoon",
  "Retail": "afternoon", "Clothing": "afternoon",
  "Entertainment": "evening", "Cosmetics": "afternoon", "Service": "morning",
};

export default function DragSlotScheduler({ itinerary, onUpdate, themeColor }) {
  const stops = itinerary?.stops || [];
  const numDays = itinerary?.num_days || 1;
  const [activeDay, setActiveDay] = useState(1);
  const [slotStops, setSlotStops] = useState(() => {
    const map = { morning: [], lunch: [], afternoon: [], evening: [] };
    stops.forEach(s => {
      const slot = s.time_slot || CAT_SLOT[s.category] || "afternoon";
      const day = s.day || 1;
      if (day === 1) map[slot]?.push(s);
    });
    return map;
  });
  const [unassigned, setUnassigned] = useState(() => {
    const assigned = new Set();
    Object.values(slotStops).forEach(arr => arr.forEach(s => assigned.add(s.business_id)));
    return stops.filter(s => !assigned.has(s.business_id) && (s.day || 1) !== 1);
  });
  const [dragging, setDragging] = useState(null);

  const moveToSlot = useCallback((stop, targetSlot) => {
    setSlotStops(prev => {
      const next = {};
      for (const [k, v] of Object.entries(prev)) {
        next[k] = v.filter(s => s.business_id !== stop.business_id);
      }
      next[targetSlot] = [...(next[targetSlot] || []), stop];
      return next;
    });
    setUnassigned(prev => prev.filter(s => s.business_id !== stop.business_id));
  }, []);

  const removeFromSlot = useCallback((stop) => {
    setSlotStops(prev => {
      const next = {};
      for (const [k, v] of Object.entries(prev)) {
        next[k] = v.filter(s => s.business_id !== stop.business_id);
      }
      return next;
    });
    setUnassigned(prev => [...prev, stop]);
  }, []);

  const totalValue = Object.values(slotStops).flat().reduce((a, s) => a + (s.value_est || 0), 0);
  const totalStops = Object.values(slotStops).flat().length;

  return (
    <div className="space-y-4" data-testid="drag-slot-scheduler">
      {/* Day selector */}
      {numDays > 1 && (
        <div className="flex items-center justify-center gap-3 mb-2">
          <button onClick={() => setActiveDay(d => Math.max(1, d - 1))} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-bold text-slate-700">Day {activeDay} of {numDays}</span>
          <button onClick={() => setActiveDay(d => Math.min(numDays, d + 1))} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}

      {/* Summary bar */}
      <div className="flex items-center justify-between bg-white rounded-xl p-3 border border-slate-100 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-lg font-black text-slate-800">{totalStops}</p>
            <p className="text-[10px] text-slate-400">Stops</p>
          </div>
          <div className="text-center">
            <p className="text-lg font-black text-green-600">${totalValue}</p>
            <p className="text-[10px] text-slate-400">Value</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unassigned.length > 0 && (
            <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-1 rounded-lg">
              {unassigned.length} unassigned
            </span>
          )}
          <SchedulerHelpSheet type="drag-slot" />
        </div>
      </div>

      {/* Time Slot Columns */}
      <div className="space-y-3">
        {SLOTS.map(slot => {
          const Icon = slot.icon;
          const items = slotStops[slot.id] || [];
          return (
            <div
              key={slot.id}
              className={`rounded-2xl border-2 border-dashed p-3 transition-colors ${dragging ? "border-sky-300 bg-sky-50/30" : "border-slate-200 bg-white"}`}
              data-testid={`slot-${slot.id}`}
              onDragOver={e => e.preventDefault()}
              onDrop={() => { if (dragging) { moveToSlot(dragging, slot.id); setDragging(null); } }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${slot.color} border`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-700">{slot.label}</p>
                  <p className="text-[10px] text-slate-400">{slot.time}</p>
                </div>
                <span className="ml-auto text-xs text-slate-400 font-medium">{items.length} stop{items.length !== 1 ? "s" : ""}</span>
              </div>

              {items.length === 0 && (
                <div className="py-4 text-center text-xs text-slate-300 border border-dashed border-slate-200 rounded-xl">
                  Drag stops here or tap + below
                </div>
              )}

              <AnimatePresence>
                {items.map((stop, idx) => (
                  <motion.div
                    key={stop.business_id}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    draggable
                    onDragStart={() => setDragging(stop)}
                    onDragEnd={() => setDragging(null)}
                    className="flex items-center gap-2 p-2 mb-1 bg-slate-50 rounded-xl border border-slate-100 cursor-grab active:cursor-grabbing hover:bg-white transition"
                    data-testid={`slot-stop-${stop.business_id}`}
                  >
                    <GripVertical className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                    <BrandLogo name={stop.name} logoUrl={stop.logo_url} size="xs" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700 truncate">{stop.name}</p>
                      <p className="text-[10px] text-slate-400">{stop.category}</p>
                    </div>
                    <span className="text-xs font-bold text-green-600">${stop.value_est || 0}</span>
                    <button onClick={() => removeFromSlot(stop)} className="w-5 h-5 rounded-full bg-red-50 hover:bg-red-100 flex items-center justify-center transition">
                      <X className="w-3 h-3 text-red-400" />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Unassigned stops */}
      {unassigned.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Unassigned Stops</p>
          <div className="grid grid-cols-2 gap-2">
            {unassigned.map(stop => (
              <motion.div
                key={stop.business_id}
                draggable
                onDragStart={() => setDragging(stop)}
                onDragEnd={() => setDragging(null)}
                className="flex items-center gap-2 p-2 bg-white rounded-xl border border-slate-200 cursor-grab hover:border-sky-300 transition"
              >
                <BrandLogo name={stop.name} logoUrl={stop.logo_url} size="xs" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-semibold text-slate-700 truncate">{stop.name}</p>
                  <div className="flex gap-2 mt-0.5">
                    {SLOTS.map(s => (
                      <button key={s.id} onClick={() => moveToSlot(stop, s.id)} className={`text-[8px] px-1.5 py-0.5 rounded ${s.color} border font-semibold hover:opacity-80`}>
                        {s.label.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
